import React, {useState} from "react"
import {ApplicationResubmitForms} from "./ReevaluatePage"
import {useModal} from "react-modal-hook"
import {useNavigate} from "react-router-dom"
import {OkDocument} from "../../resources/common"
import {BusinessApplication, Officer} from "../../resources/application"
import ReevaluateOfficerForm, {
	ReevaluatedOfficerApplicationData,
} from "../../components/ReevaluateApplication/Forms/ReevaluateOfficerForm/ReevaluateOfficerForm"
import ReevaluateOfficerConfirmationModal from "../../components/ReevaluateApplication/ReevaluateConfirmationModal/ReevaluateBusinessApplicationConfirmationModal/ReevaluateOfficerConfirmationModal"
import {MetaItemProps} from "../../components/TitleBar/TitleBar"
import ApplicationDetails, {identificationFieldBeforeUpdateRequest} from "./ApplicationDetails"
import Admonition from "../../components/Admonitions/Admonition"

interface ReevaluateOfficerPageProps {
	application: OkDocument<BusinessApplication>
	enableReevaluateApplication: boolean
	officer: Officer
	officerId: string
}

function toOfficerFormData(officer?: Officer): ReevaluatedOfficerApplicationData | undefined {
	if (!officer || !officer.address || !officer.dateOfBirth || !officer.email || !officer.phone) {
		return undefined
	}

	return {
		fullName: officer.fullName,
		title: officer.title,
		address: officer.address,
		ssn: officer.ssn,
		nationality: officer.nationality,
		passport: officer.passport,
		matriculaConsular: officer.matriculaConsular,
		dateOfBirth: officer.dateOfBirth,
		email: officer.email,
		phone: officer.phone,
		occupation: officer.occupation,
		sourceOfIncome: officer.sourceOfIncome,
		annualIncome: officer.annualIncome,
	}
}

export default function ReevaluateOfficerContainer({
	application,
	enableReevaluateApplication,
	officer,
	officerId,
}: ReevaluateOfficerPageProps) {
	const navigate = useNavigate()

	const [officerReevaluatedData, setOfficerReevaluatedData] = useState<ReevaluatedOfficerApplicationData>(
		officer as ReevaluatedOfficerApplicationData
	)

	const [showOfficerConfirmationModal, closeOfficerConfirmationModal] = useModal(
		() => (
			<ReevaluateOfficerConfirmationModal
				onSuccess={() => navigate(`/applications/${application.data.id}`)}
				close={closeOfficerConfirmationModal}
				application={application.data}
				officerId={officerId}
				enableReevaluateApplication={enableReevaluateApplication}
				reevaluatedData={officerReevaluatedData}
			/>
		),
		[officerReevaluatedData]
	)

	const title = application.data.attributes.name
	const meta: MetaItemProps[] = [{text: "Business", icon: "toolbox"}]
	const officerFormData = toOfficerFormData(application.data.attributes.officer)

	const form = (
		<ApplicationDetails
			title={title}
			meta={meta}
			application={application}
			formId={ApplicationResubmitForms.officerForm}
		>
			{officerFormData && (
				<ReevaluateOfficerForm
					officer={officerFormData}
					submitCallback={(values) => {
						const currentValues = {
							ssn: application.data.attributes.officer?.ssn,
							passport: application.data.attributes.officer?.passport,
							matriculaConsular: application.data.attributes.officer?.matriculaConsular,
						}
						const identificationFields = identificationFieldBeforeUpdateRequest({
							currentValues: currentValues,
							updatedValues: values,
						})

						setOfficerReevaluatedData({
							...values,
							...identificationFields,
						})
						showOfficerConfirmationModal()
					}}
					formId={ApplicationResubmitForms.officerForm}
				/>
			)}
			{application.data.attributes.publicTradedCompany && (
				<Admonition type="is-danger" title="Invalid application type, does not have an officer" message={""} />
			)}
		</ApplicationDetails>
	)

	const error = (
		<Admonition
			type="is-danger"
			title="Could not be able to show resubmit form, please reach administator."
			message={""}
		/>
	)

	if (!officerFormData) {
		return error
	} else {
		return form
	}
}
